import React, { useState, useEffect, useRef } from 'react';
import './CutScan.css';
import { analyzeCuts, isCutScanSupported } from '../utils/cutDetector';
import { Scissors, CheckCircle, AlertTriangle, Sunset, Shuffle, Ban, X } from 'lucide-react';

/**
 * CutScan - button-triggered, whole-clip cut detection for a video. Verifies a
 * clip is a single continuous take and lists any cuts. Heavy (decodes the whole
 * clip), so it runs on demand with a progress bar.
 *
 * @param {File} file
 * @param {object|null} compareResult - the other side's cut result (compare tool)
 * @param {(result:object|null)=>void} [onResult] - lift result to parent for diffing
 */
function CutScan({ file, compareResult, onResult }) {
    const [status, setStatus] = useState('idle'); // idle | scanning | done | error
    const [progress, setProgress] = useState(0);
    const [result, setResult] = useState(null);
    const abortRef = useRef(null);
    const onResultRef = useRef(onResult);
    useEffect(() => { onResultRef.current = onResult; });

    // Reset whenever the file changes.
    useEffect(() => {
        setStatus('idle');
        setProgress(0);
        setResult(null);
        onResultRef.current?.(null);
        return () => abortRef.current?.abort();
    }, [file]);

    if (!file?.type?.startsWith('video/')) return null;

    const isISOBMFF = file.type === 'video/mp4'
        || file.type === 'video/quicktime'
        || /\.(mp4|mov|m4v)$/i.test(file.name || '');
    const supported = isCutScanSupported() && isISOBMFF;

    const runScan = async () => {
        const controller = new AbortController();
        abortRef.current = controller;
        setStatus('scanning');
        setProgress(0);
        try {
            const r = await analyzeCuts(file, {
                onProgress: (f) => setProgress(f),
                signal: controller.signal,
            });
            if (controller.signal.aborted) { setStatus('idle'); return; }
            setResult(r);
            onResultRef.current?.(r);
            setStatus(r.scanned ? 'done' : 'error');
        } catch (e) {
            console.error('Cut scan failed:', e);
            setStatus('error');
        }
    };

    const cancel = () => {
        abortRef.current?.abort();
        setStatus('idle');
        setProgress(0);
    };

    return (
        <div className="cut-scan">
            <div className="cut-scan-header">
                <Scissors size={12} className="cut-scan-header-icon" />
                <span>Cut Detection</span>
            </div>

            {status === 'idle' && (
                supported ? (
                    <button className="cut-scan-btn" onClick={runScan}>
                        <Scissors size={13} /> Scan for cuts
                    </button>
                ) : (
                    <div className="cut-scan-row cut-scan-na">
                        <Ban size={13} className="cut-scan-icon" />
                        <span>{isISOBMFF
                            ? 'Cut scan unavailable — WebCodecs not supported here'
                            : 'Cut scan supports MP4 / MOV files'}</span>
                    </div>
                )
            )}

            {status === 'scanning' && (
                <div className="cut-scan-progress-wrap">
                    <div className="cut-scan-progress-track">
                        <div className="cut-scan-progress-fill" style={{ width: `${Math.round(progress * 100)}%` }} />
                    </div>
                    <span className="cut-scan-progress-pct">{Math.round(progress * 100)}%</span>
                    <button className="cut-scan-cancel" onClick={cancel} title="Cancel scan">
                        <X size={12} />
                    </button>
                </div>
            )}

            {status === 'error' && (
                <div className="cut-scan-row cut-scan-na">
                    <Ban size={13} className="cut-scan-icon" />
                    <span>{reasonMessage(result?.reason)}</span>
                </div>
            )}

            {status === 'done' && result && (
                <CutResults result={result} compareResult={compareResult} />
            )}
        </div>
    );
}

function CutResults({ result, compareResult }) {
    const hardCuts = result.cuts.filter((c) => c.type === 'hard');
    const fades = result.cuts.filter((c) => c.type === 'fade');
    const jumps = result.cuts.filter((c) => c.type === 'jump');

    // Compare-mode: flag when the hard-cut count differs from the other side.
    const otherHard = compareResult?.scanned
        ? compareResult.cuts.filter((c) => c.type === 'hard').length
        : null;
    const isDiff = otherHard !== null && otherHard !== hardCuts.length;

    if (result.continuous && fades.length === 0 && jumps.length === 0) {
        return (
            <div className={`cut-scan-row cut-scan-ok ${isDiff ? 'cut-scan-diff' : ''}`}>
                <CheckCircle size={13} className="cut-scan-icon" />
                <span className="cut-scan-text">
                    Continuous take — no cuts ({result.framesAnalyzed} frames)
                </span>
                {isDiff && <span className="cut-scan-diff-dot">●</span>}
            </div>
        );
    }

    return (
        <div className="cut-scan-results">
            <div className={`cut-scan-row cut-scan-summary ${isDiff ? 'cut-scan-diff' : ''}`}>
                <AlertTriangle size={13} className="cut-scan-icon" />
                <span className="cut-scan-text">
                    {hardCuts.length} cut{hardCuts.length === 1 ? '' : 's'}
                    {fades.length > 0 && ` · ${fades.length} fade${fades.length === 1 ? '' : 's'}`}
                    {jumps.length > 0 && ` · ${jumps.length} possible jump${jumps.length === 1 ? '' : 's'}`}
                </span>
                {isDiff && <span className="cut-scan-diff-dot">●</span>}
            </div>
            <div className="cut-scan-list">
                {result.cuts.map((c, i) => (
                    <div key={i} className={`cut-scan-item cut-scan-item-${c.type}`}>
                        {cutIcon(c.type)}
                        <span className="cut-scan-time">{formatTime(c.time)}</span>
                        <span className="cut-scan-type">{cutLabel(c.type)}</span>
                        <span className="cut-scan-conf">{Math.round(c.confidence * 100)}%</span>
                    </div>
                ))}
            </div>
            {result.truncated && (
                <div className="cut-scan-note">Scan capped at {result.framesAnalyzed} frames</div>
            )}
        </div>
    );
}

function cutIcon(type) {
    if (type === 'hard') return <Scissors size={11} className="cut-scan-item-icon" />;
    if (type === 'fade') return <Sunset size={11} className="cut-scan-item-icon" />;
    return <Shuffle size={11} className="cut-scan-item-icon" />; // jump
}

function cutLabel(type) {
    if (type === 'hard') return 'cut';
    if (type === 'fade') return 'fade';
    return 'jump?';
}

function reasonMessage(reason) {
    switch (reason) {
        case 'unsupported-container': return 'Cut scan supports MP4 / MOV files';
        case 'unsupported-codec':
        case 'webcodecs-unsupported': return 'Cut scan unavailable — codec not decodable in this browser';
        default: return 'Cut scan failed for this file';
    }
}

function formatTime(t) {
    const m = Math.floor(t / 60);
    const s = t - m * 60;
    return m > 0 ? `${m}:${s.toFixed(1).padStart(4, '0')}` : `${s.toFixed(2)}s`;
}

export default CutScan;
